import React from "react";
import { Button } from "primereact/button";
import { DateRangeFilter } from "./DateRangeFilter";
import {
  CUSTOM_PERIOD,
  CUSTOM_PERIOD_DESCRIPTION,
  CUSTOM_PERIOD_LABEL,
} from "../../utils/constants/audit-report-constants";
import {
  REPORT_DATE_RANGE_HINT,
  getMaxReportDateTo,
  getMinReportDateFrom,
} from "../../utils/constants/report-date-range";

export interface PeriodPresetOption<K extends string = string> {
  key: K;
  label: string;
  /** Qué rango deja el preset, para el tooltip del botón. */
  description: string;
}

/**
 * Lo que la fila de botones necesita saber del período. Lo cumple
 * `useReportPeriod` (reportes), que agrega lo suyo encima —`grain` y el tope de
 * meses— sin que este componente tenga que enterarse.
 */
export interface PeriodFilterModel<K extends string = string> {
  today: Date;
  presets: PeriodPresetOption<K>[];
  period: K | typeof CUSTOM_PERIOD | null;
  dateFrom: Date | null;
  dateTo: Date | null;
  applyPreset: (preset: K) => void;
  enableCustomPeriod: () => void;
  handleDateFromChange: (date: Date | null) => void;
  handleDateToChange: (date: Date | null) => void;
  /** Solo los reportes agrupan por bucket; el listado de asistencias no. */
  grain?: string;
}

interface ReportPeriodFilterProps<K extends string> {
  /** Lo devuelve `useReportPeriod`. */
  period: PeriodFilterModel<K>;
  disabled?: boolean;
  /** Prefijo de ids y data-testid, para distinguir dos filas en la misma página. */
  idPrefix: string;
  /**
   * Aclara el agrupamiento que dejó el preset. Solo los reportes que agrupan por
   * bucket: el export de eventos es una fila por evento y no agrupa nada.
   */
  showGrain?: boolean;
  /**
   * Aclaración debajo de los botones. Por defecto el tope de rango de los
   * reportes; una pantalla sin ese tope pasa la suya.
   */
  hint?: string;
  /** Tooltip del botón "Personalizado". Por defecto el de los reportes. */
  customDescription?: string;
  /**
   * Topes del rango a mano. Sin pasarlos, aplica la ventana de 6 meses de los
   * reportes. `null` deja ese extremo sin tope.
   */
  minDateFrom?: Date | null;
  maxDateTo?: Date | null;
}

/**
 * Fila de períodos: los presets del catálogo de la pantalla más
 * "Personalizado".
 *
 * Es un componente y no markup repetido en cada pantalla porque el punto es que
 * todas se vean y se comporten igual — cuando el export de eventos tenía su
 * propia versión, cambiar de tipo en el selector cambiaba de interfaz. Por eso
 * lo que cambia entre pantallas (el catálogo de presets, el tope del rango, el
 * texto de ayuda) entra por props y no se clona el componente.
 *
 * No hay control de grano: el botón define rango y agrupamiento juntos. Es lo
 * único que hace que los botones tengan sentido — "última semana" agrupado por
 * mes daría una sola fila con una semana de datos adentro.
 */
export function ReportPeriodFilter<K extends string>({
  period,
  disabled = false,
  idPrefix,
  showGrain = false,
  hint = REPORT_DATE_RANGE_HINT,
  customDescription = CUSTOM_PERIOD_DESCRIPTION,
  minDateFrom,
  maxDateTo,
}: ReportPeriodFilterProps<K>): React.ReactElement {
  const resolvedMaxDateTo =
    maxDateTo === undefined
      ? getMaxReportDateTo(period.dateFrom ?? period.today, period.today)
      : maxDateTo ?? undefined;
  const resolvedMinDateFrom =
    minDateFrom === undefined
      ? period.dateTo
        ? getMinReportDateFrom(period.dateTo)
        : undefined
      : minDateFrom ?? undefined;
  const periodHint = [
    showGrain && period.grain
      ? `Agrupado por ${period.grain === "week" ? "semana" : "mes"}.`
      : "",
    hint,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <div className="mb-3">
        <span className="block font-medium mb-2">Período</span>
        <div className="flex flex-wrap align-items-center gap-2">
          {period.presets.map((preset) => (
            <Button
              key={preset.key}
              type="button"
              label={preset.label}
              outlined={period.period !== preset.key}
              size="small"
              disabled={disabled}
              onClick={() => period.applyPreset(preset.key)}
              tooltip={preset.description}
              tooltipOptions={{ position: "top" }}
              data-testid={`${idPrefix}-preset-${preset.key}`}
            />
          ))}
          <Button
            type="button"
            label={CUSTOM_PERIOD_LABEL}
            icon="pi pi-calendar"
            outlined={period.period !== CUSTOM_PERIOD}
            size="small"
            disabled={disabled}
            onClick={period.enableCustomPeriod}
            tooltip={customDescription}
            tooltipOptions={{ position: "top" }}
            data-testid={`${idPrefix}-preset-custom`}
          />
        </div>

        {/* El tope de 6 meses es una regla del PERÍODO, no del archivo: explica
            por qué no se puede estirar el rango, así que se lee acá y no colgado
            del botón de exportar. */}
        {periodHint ? (
          <p className="text-sm text-color-secondary mt-2 mb-0">{periodHint}</p>
        ) : null}
      </div>

      {/* Solo con "Personalizado" elegido: en los presets el rango ya está dado
          y dos selectores de fecha al lado solo invitan a desincronizarlos. */}
      {period.period === CUSTOM_PERIOD && (
        <div className="grid mb-3" data-testid={`${idPrefix}-custom-range`}>
          <DateRangeFilter
            variant="stacked"
            dateFrom={period.dateFrom}
            dateTo={period.dateTo}
            onDateFromChange={period.handleDateFromChange}
            onDateToChange={period.handleDateToChange}
            disabled={disabled}
            startDateId={`${idPrefix}-date-from`}
            endDateId={`${idPrefix}-date-to`}
            maxDateFrom={period.dateTo ?? resolvedMaxDateTo}
            maxDateTo={resolvedMaxDateTo}
            minDateFrom={resolvedMinDateFrom}
            minDateTo={period.dateFrom ?? undefined}
          />
        </div>
      )}
    </>
  );
}
